// src/components/admin/SecurityManagement.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Search, Download, Loader2, 
  Users, Clock, Shield, Activity, AlertTriangle
} from 'lucide-react';
import { Alert, AlertDescription } from "../../components/ui/alert";

const SecurityManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activityData, setActivityData] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userList, setUserList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [timeRange, setTimeRange] = useState('24h');
  const [error, setError] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [usersResponse, activityResponse] = await Promise.all([
          fetch('/api/admin/users', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }),
          fetch('/api/admin/activity-logs', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          })
        ]);

        if (!usersResponse.ok || !activityResponse.ok) {
          throw new Error('Failed to fetch security data');
        }

        const [users, activity] = await Promise.all([
          usersResponse.json(),
          activityResponse.json()
        ]);

        // Extract users array from the response object
        setUserList(users.users || users); // This handles both formats

        setActivityData(activity);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter activity based on search, type, and time range
  const filteredActivity = activityData.filter(activity => {
    const matchesSearch = searchTerm === '' || 
      activity.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.action?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === 'all' || activity.type === filterType;

    const activityDate = new Date(activity.createdAt);
    const now = new Date();
    const hoursDiff = (now - activityDate) / (1000 * 60 * 60);
    
    const matchesTime = 
      (timeRange === '24h' && hoursDiff <= 24) ||
      (timeRange === '7d' && hoursDiff <= 168) ||
      (timeRange === '30d' && hoursDiff <= 720) ||
      timeRange === 'all';

    return matchesSearch && matchesType && matchesTime;
  });

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const response = await fetch('/api/admin/security/export', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Export failed');

      // Get the filename from the Content-Disposition header if available
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'security-report.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      } else {
        // Use default filename with date
        filename = `security-report-${new Date().toISOString().split('T')[0]}.csv`;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setExportLoading(false);
    } catch (err) {
      setError('Failed to export data');
      setExportLoading(false);
    }
  };

  if (!user?.isAdmin) {
    return <div>Admin access required</div>;
  }

  return (
    <div className="space-y-6">

    {/* Header */}
    <div className="">
      <h2 className="text-2xl font-semibold">Security Dashboard</h2>
      <div className="bg-white px-4 py-6 rounded-xl mt-6">
        <div className="mb-6">
          <p className="font-semibold text-gray-600">Monitor user activity and security events</p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500">Total Users</p>
                <p className="text-xl font-semibold">{userList.length}</p>
              </div>
              <Users className="h-6 w-6 text-blue-500" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500">Active Now</p>
                <p className="text-xl font-semibold">
                  {userList.filter(u => 
                    u.lastLogin && new Date(u.lastLogin) > new Date(Date.now() - 15 * 60000)
                  ).length}
                </p>
              </div>
              <Clock className="h-6 w-6 text-green-500" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500">Security Events</p>
                <p className="text-xl font-semibold">
                  {activityData.filter(a => a.type === 'security').length}
                </p>
              </div>
              <Shield className="h-6 w-6 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500">Failed Logins</p>
                <p className="text-xl font-semibold">
                  {activityData.filter(a => a.action === 'login_failed').length}
                </p>
              </div>
              <AlertTriangle className="h-6 w-6 text-red-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
          <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search users or activities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex flex-wrap gap-3 w-full md:w-auto">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 flex-1 md:flex-none"
              >
                <option value="all">All Types</option>
                <option value="login">Logins</option>
                <option value="profile">Profile Updates</option>
                <option value="security">Security Events</option>
              </select>

              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 pr-7 flex-1 md:flex-none"
              >
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="all">All Time</option>
              </select>

              <button
                onClick={handleExport}
                disabled={exportLoading}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 flex-1 md:flex-none justify-center"
              >
                {exportLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Activity Log */}
        {loading ? (
          <div className="flex justify-center items-center h-48">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="overflow-x-auto">
              {/* Mobile-friendly table layout */}
              <div className="block md:hidden">
                {filteredActivity.map((activity, index) => (
                  <div key={index} className="border-b p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className={`px-2 py-1 text-xs leading-5 font-semibold rounded-full ${
                        activity.type === 'security' ? 'bg-red-100 text-red-800' :
                        activity.type === 'login' ? 'bg-green-100 text-green-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {activity.action}
                      </span>
                      <span className="text-sm text-gray-500">
                        {new Date(activity.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      {activity.user?.email || 'System'}
                    </p>
                    <p className="text-sm text-gray-700 mb-1">{activity.details}</p>
                    <p className="text-xs text-gray-500">IP: {activity.ipAddress}</p>
                  </div>
                ))}
                {filteredActivity.length === 0 && (
                  <div className="p-4 text-center text-gray-500">
                    No activity found matching your filters
                  </div>
                )}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        IP Address
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredActivity.map((activity, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {activity.user?.email || 'System'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            activity.type === 'security' ? 'bg-red-100 text-red-800' :
                            activity.type === 'login' ? 'bg-green-100 text-green-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {activity.action}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {activity.details}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {activity.ipAddress}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(activity.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                    {filteredActivity.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                          No activity found matching your filters
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default SecurityManagement;